import { useState } from "preact/hooks"
import useSWR from "swr"
import { Check, Pencil, Plus, Trash2 } from "lucide-react"
import {
  ContractTerm,
  ContractTermInput,
  contract_terms_key,
  create_contract_term,
  delete_contract_term,
  list_contract_terms,
  update_contract_term,
} from "@/api/contract_term"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ContractTermDialog } from "./contract_term_dialog"
import { format_effective_window, format_term_rate, term_to_form } from "./contract_term_format"

interface ContractTermsSectionProps {
  /** Terms read from / attached to this contract document. */
  contract_document_id: string
  className?: string
}

function StatusBadge({ status }: { status: ContractTerm["status"] }) {
  return status === "confirmed" ? <Badge variant="success">Confirmed</Badge> : <Badge variant="warning">Suggested</Badge>
}

interface TermRowProps {
  term: ContractTerm
  onConfirm: (term: ContractTerm) => void
  onEdit: (term: ContractTerm) => void
  onDelete: (term: ContractTerm) => void
}

function TermRow({ term, onConfirm, onEdit, onDelete }: TermRowProps) {
  return (
    <TableRow class="align-top">
      <TableCell>
        <StatusBadge status={term.status} />
      </TableCell>
      <TableCell class="min-w-48">
        <div class="font-medium text-gray-900">{term.program}</div>
        <div class="text-xs text-gray-500">
          {[term.retailer_name, term.match_pattern && `/${term.match_pattern}/i`].filter(Boolean).join(" · ")}
        </div>
        {term.source_excerpt && (
          <blockquote class="mt-1 border-l-2 border-gray-300 pl-2 text-xs italic text-gray-600">{term.source_excerpt}</blockquote>
        )}
        {term.notes && <div class="mt-1 text-xs text-gray-500">{term.notes}</div>}
      </TableCell>
      <TableCell>{format_term_rate(term)}</TableCell>
      <TableCell class="text-gray-600">{format_effective_window(term.effective_from, term.effective_to)}</TableCell>
      <TableCell>
        <div class="flex flex-col items-end gap-1">
          {term.status === "suggested" && (
            <Button variant="outline" size="sm" onClick={() => onConfirm(term)} title="Confirm term">
              <Check className="h-4 w-4 mr-1" />
              Confirm
            </Button>
          )}
          <div class="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => onEdit(term)} title="Edit term">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(term)} title="Delete term">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </TableCell>
    </TableRow>
  )
}

/** List, add, edit, confirm and delete the contract terms read from one contract document. */
export function ContractTermsSection({ contract_document_id, className }: ContractTermsSectionProps) {
  const params = { contract_document_id }
  const { data: terms, error, isLoading, mutate } = useSWR<ContractTerm[]>(contract_terms_key(params), () => list_contract_terms(params))
  const [editing, set_editing] = useState<ContractTerm | null>(null)
  const [adding, set_adding] = useState(false)
  const [action_error, set_action_error] = useState<string | null>(null)

  async function run(action: () => Promise<unknown>) {
    set_action_error(null)
    try {
      await action()
      await mutate()
    } catch (e) {
      set_action_error(e instanceof Error ? e.message : "Something went wrong")
    }
  }

  function handle_confirm(term: ContractTerm) {
    run(() => update_contract_term(term.id, { status: "confirmed" }))
  }

  function handle_delete(term: ContractTerm) {
    if (!window.confirm(`Delete the "${term.program}" term?`)) return
    run(() => delete_contract_term(term.id))
  }

  async function handle_create(input: ContractTermInput) {
    // The server scopes the term to the contract's distributor.
    await create_contract_term({ ...input, contract_document_id })
    await mutate()
  }

  async function handle_update(input: ContractTermInput) {
    if (!editing) return
    await update_contract_term(editing.id, input)
    await mutate()
  }

  const suggested_count = (terms ?? []).filter((t: ContractTerm) => t.status === "suggested").length

  return (
    <div class={`bg-white border rounded-lg p-6 ${className ?? ""}`}>
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-semibold">Contract Terms</h2>
          <p class="text-sm text-gray-500">
            Signed rates that chargebacks are checked against.
            {suggested_count > 0 && ` ${suggested_count} suggested by analysis need review.`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => set_adding(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Add term
        </Button>
      </div>

      {action_error && <p class="mb-3 text-sm text-red-600">{action_error}</p>}

      {isLoading ? (
        <p class="py-6 text-center text-sm text-gray-500">Loading terms...</p>
      ) : error ? (
        <p class="py-6 text-center text-sm text-red-600">Failed to load contract terms.</p>
      ) : !terms || terms.length === 0 ? (
        <p class="py-6 text-center text-sm text-gray-500">No terms yet. Analyze the contract to suggest them, or add one.</p>
      ) : (
        <Table class="text-sm">
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Program</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>Effective</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {terms.map((term: ContractTerm) => (
              <TermRow key={term.id} term={term} onConfirm={handle_confirm} onEdit={set_editing} onDelete={handle_delete} />
            ))}
          </TableBody>
        </Table>
      )}

      <ContractTermDialog open={adding} onOpenChange={set_adding} title="Add contract term" onSave={handle_create} />
      <ContractTermDialog
        open={editing != null}
        onOpenChange={(open: boolean) => !open && set_editing(null)}
        initial={editing ? term_to_form(editing) : undefined}
        title="Edit contract term"
        onSave={handle_update}
      />
    </div>
  )
}
